import { Component, OnInit, Input } from '@angular/core';
import { isMaintainXWorkOrder, PopupObservationService } from '@services/observationDetail/popup-observation.service';
import { Router } from '@angular/router';
import { PopoverService } from '@services/popover/popover.service';
import { Feature } from '@services/subscriber/subscriber.service';
import { AssetsService, CommsService, LoadingService, SubscriberService, ToastService } from '@services';
import { ObservationService } from '@services/observations/observation.service';
import { awaitHandler } from '@utils/awaitHandler';
import { NGXLogger } from 'ngx-logger';

export interface MaintainXCustomStatus {
  customStatusID: number;
  label: string;
  baseAssetStatus: string;
}

@Component({
  selector: 'app-workorder-popup',
  templateUrl: './workorder-popup.component.html',
  styleUrls: ['./workorder-popup.component.scss'],
})
export class WorkorderPopupComponent implements OnInit {
  @Input() observationID: Number;
  @Input() workOrder: any;
  public workOrderNumber = '';
  public workRequestTitle = '';
  public priority = 'NONE';
  public description = '';
  public usesFiix = false;
  public usesMaintainX = false;
  public observation: { [key: string]: any };
  public isEdit = false;
  public loadingWorkRequest = false;
  // a failed prefill must not let Save blank the existing MaintainX request
  public loadFailed = false;
  // the MaintainX asset status (CVX-6603): offered only when the request's
  // subject is a MaintainX asset; '' sends nothing
  public showAssetStatus = false;
  public assetStatus = '';
  public downtimeType = 'UNPLANNED';
  public customStatusID = 0;
  public customStatusOptions: MaintainXCustomStatus[] = [];
  private customStatuses: MaintainXCustomStatus[] = [];
  // the status edit mode prefilled; Save leaves it alone unless it was changed,
  // so a stale copy never overwrites a newer status set in MaintainX
  private prefilledStatus = '';

  constructor(private popoverService: PopoverService,
              private loading: LoadingService,
              private logger: NGXLogger,
              private subscriberService: SubscriberService,
              private popOps: PopupObservationService,
              private comms: CommsService,
              private router: Router,
              private observationService: ObservationService,
              private toastService: ToastService,
              private assetsService: AssetsService) {
  }

  async ngOnInit(): Promise<void> {
    if (this.workOrder) {
      this.workOrderNumber = this.workOrder;
    }
    // Fiix has no edit endpoint, so an existing work order keeps the manual form
    this.usesFiix = this.subscriberService.usesFeature(Feature.OBSERVATION_FIIX) && !this.workOrder;
    // a hand-entered (non-MaintainX) work order keeps the manual form even when
    // MaintainX is on, so it can still be edited
    this.observation = this.observationService.getObservationById(+this.observationID);
    if (!this.subscriberService.usesFeature(Feature.OBSERVATION_FIIX)
      && this.subscriberService.usesFeature(Feature.OBSERVATION_MAINTAINX)
      && (!this.workOrder || isMaintainXWorkOrder(this.workOrder, this.observation?.workorderStatus))) {
      this.usesMaintainX = true;
    }
    if (this.usesMaintainX && this.workOrder) {
      this.isEdit = true;
      await this.loadWorkRequest();
    } else if (this.usesMaintainX && this.observation?.subjectType === 'asset') {
      // the server links an asset whose Asset ID is a MaintainX asset number
      const asset = this.assetsService.getAssetById(+this.observation.subjectID)[0];
      this.showAssetStatus = /^\d+$/.test((asset?.identifier || '').trim());
    }
    if (this.showAssetStatus) {
      await this.loadCustomStatuses();
    }
  }

  // the organization's own status labels; without them only the base statuses are offered
  private async loadCustomStatuses(): Promise<void> {
    const [data] = await awaitHandler(this.comms.sendMessage({ cmd: 'getMaintainXAssetStatuses' }, false, false));
    this.customStatuses = data?.reqStatus === 'OK' ? data.result?.customStatuses || [] : [];
    // keep a prefilled custom status even if the list could not be loaded
    this.customStatusOptions = this.customStatuses.filter(s => s.baseAssetStatus === this.assetStatus);
  }

  public onAssetStatusChange(status: string): void {
    this.assetStatus = status;
    this.customStatusOptions = this.customStatuses.filter(s => s.baseAssetStatus === this.assetStatus);
    this.customStatusID = 0;
  }

  private addAssetStatus(req: any): void {
    if (!this.showAssetStatus || !this.assetStatus || (this.isEdit && this.statusKey() === this.prefilledStatus)) {
      return;
    }
    req.assetStatus = this.assetStatus;
    if (this.assetStatus === 'OFFLINE') {
      req.downtimeType = this.downtimeType;
    }
    if (this.customStatusID) {
      req.customStatusID = this.customStatusID;
    }
  }

  private statusKey(): string {
    return [this.assetStatus, this.assetStatus === 'OFFLINE' ? this.downtimeType : '', this.customStatusID].join('|');
  }

  // the request was saved even when MaintainX refused the asset status; say so
  private warnIfStatusNotSet(data: any): void {
    const [, reason] = (data?.reqStatusText || '').split('asset status not set: ');
    if (reason !== undefined) {
      this.toastService.showError(reason, 'SHARED.Work_Request_Asset_Status_Not_Set');
    }
  }

  private async loadWorkRequest(): Promise<void> {
    this.loadingWorkRequest = true;
    const [data, err] = await awaitHandler(this.comms.sendMessage({
      cmd: 'getWorkRequest',
      observationID: this.observationID
    }, false, false));
    this.loadingWorkRequest = false;
    const workRequest = data?.reqStatus === 'OK' ? data.result?.workRequest : null;
    if (!workRequest) {
      this.loadFailed = true;
      this.showFailure(data, err);
      return;
    }
    this.priority = workRequest.priority || 'NONE';
    this.workRequestTitle = workRequest.title || '';
    this.description = workRequest.description || '';
    // prefill the asset's current status; Save sends it only if it is changed
    this.showAssetStatus = !!workRequest.assetId;
    this.assetStatus = workRequest.assetStatus?.status || '';
    this.downtimeType = workRequest.assetStatus?.downtimeType || 'UNPLANNED';
    this.customStatusID = workRequest.assetStatus?.customStatusID || 0;
    this.prefilledStatus = this.statusKey();
  }

  public async close(): Promise<void> {
    await this.popoverService.safeDismiss();
  }

  public async createWorkOrder(): Promise<void> {
    await this.popoverService.safeDismiss();
    // Fiix, like MaintainX: the server records the work order on the observation,
    // so a failure is shown and changes nothing, and success only refreshes
    if (this.usesFiix) {
      const fiixReq: any = {
        cmd: 'createFiixWorkOrder',
        observationID: this.observationID,
        title: this.workRequestTitle,
        description: this.description,
        priority: this.priority
      };
      // no assetID: the server attaches the observation's own asset when it is linked in Fiix
      const [data, err] = await awaitHandler(this.comms.sendMessage(fiixReq, false, false));
      if (!data || data.reqStatus !== 'OK') {
        this.showFailure(data, err);
        return;
      }
      this.loading.enable('Request Sent', 1500);
      await this.popOps.refreshAfterWorkRequest(+this.observationID, false);
      return;
    }

    if (!this.usesMaintainX) {
      this.popOps.createWorkOrder({
        observationID: this.observationID,
        workorder: this.workOrderNumber
      }, this.workOrder);
      return;
    }

    // MaintainX: the server sets state/workorder on the observation itself,
    // so only refresh afterwards — never a second updateObservation.
    if (this.isEdit) {
      if (this.loadFailed) {
        return;
      }
      // Edit the existing request; createWorkRequest here would duplicate it.
      const updObj: any = {
        cmd: 'updateWorkRequest',
        observationID: this.observationID,
        title: this.workRequestTitle,
        description: this.description,
        priority: this.priority
      };
      this.addAssetStatus(updObj);
      const [res, updErr] = await awaitHandler(this.comms.sendMessage(updObj, false, false));
      if (!res || res.reqStatus !== 'OK') {
        this.showFailure(res, updErr);
        return;
      }
      this.warnIfStatusNotSet(res);
      await this.popOps.refreshAfterWorkRequest(+this.observationID, true);
      return;
    }

    const reqObj: any = {
      cmd: 'createWorkRequest',
      observationID: this.observationID,
      locationID: this.observation?.locationID || this.subscriberService.locationID(),
      priority: this.priority,
      description: this.description
    };
    if (this.observation?.subjectType && this.observation.subjectID) {
      reqObj.subjectType = this.observation.subjectType;
      reqObj.subjectID = this.observation.subjectID;
    }
    if (this.workRequestTitle) {
      reqObj.title = this.workRequestTitle;
    }
    this.addAssetStatus(reqObj);
    const [data, err] = await awaitHandler(this.comms.sendMessage(reqObj, false, false));
    if (!data || data.reqStatus !== 'OK') {
      this.showFailure(data, err);
      return;
    }
    this.loading.enable('Request Sent', 1500);
    this.warnIfStatusNotSet(data);
    await this.popOps.refreshAfterWorkRequest(+this.observationID, false);
  }

  private showFailure(data: any, err: any): void {
    this.logger.error(err || data?.reqStatusText);
    const message = data?.reqStatusText;
    this.toastService.showError('SHARED.Update_Failed', undefined, message ? { message } : undefined);
  }

}
